// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Pagination, { PaginationPresentationProps } from "@awc/MobiusOverrides/Pagination";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    wishListDataView: getWishListState(state, state.pages.myListDetails.wishListId),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
});

const mapDispatchToProps = {
    updateLoadWishListLinesParameter,
    loadWishListLines,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsOptionsStyles {
    paginationContainer?: GridContainerProps;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;
}

export const myListsDetailsOptionsStyles: MyListsDetailsOptionsStyles = {
    paginationContainer: {
        css: css`
            margin: 0;
        `,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};

const styles = myListsDetailsOptionsStyles;

const MyListDetailsPagination: React.FC<Props> = ({
    wishListDataView,
    wishListLinesDataView,
    updateLoadWishListLinesParameter,
    loadWishListLines,
}) => {
    if (
        !wishListDataView.value ||
        !wishListLinesDataView.value ||
        wishListLinesDataView.isLoading ||
        !wishListLinesDataView.pagination
    ) {
        return null;
    }

    const changePageHandler = (newPageIndex: number) => {
        updateLoadWishListLinesParameter({ page: newPageIndex });
        loadWishListLines();
    };

    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateLoadWishListLinesParameter({ pageSize: newPageSize });
        loadWishListLines();
    };

    const pagination = wishListLinesDataView.pagination;

    // XNG-Changes: hide pagination when there are no products
    if (pagination.totalItemCount < 1) {
        return null;
    }
    // XNG-Changes:remove GridContainer
    return (
        // <GridContainer {...styles.paginationContainer}>
        //     <GridItem {...styles.paginationGridItem} data-test-selector="myListDetails_pagination">
        <Pagination
            {...styles.pagination}
            currentPage={pagination.currentPage}
            resultsPerPage={pagination.pageSize}
            resultsCount={pagination.totalItemCount}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePageHandler}
            onChangeResultsPerPage={changeResultsPerPageHandler}
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
            data-test-selector="myListDetails_pagination"
            // XNG-Changes
            xBreakPoint={false}
        />
        //      </GridItem>
        // </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListDetailsPagination),
    definition: {
        group: "My Lists Details",
        displayName: "Pagination",
        allowedContexts: ["MyListsDetailsPage"],
    },
};

export default widgetModule;
